import { publicAsset } from "@/lib/publicAsset";

/** Pastas em `public/images/Vips_rustIcones/` (sincronizadas de `images/Vips_rustIcones/`). */
export const VIP_KIT_FOLDERS = {
    bronze: "Vip-Bronze",
    prata: "Vip-Prata",
    ouro: "Vip-Ouro",
    apoiador: "Vip-Apoiador",
};

/** Ordem dos slots de equipamento (igual ao simulador). */
export const ARMOR_SLOT_LABELS = [
    "Cabeça",
    "Máscara",
    "Tronco",
    "Cintura",
    "Mãos",
    "Pernas",
    "Pés",
];

const INV_COLS = 6;

/** @typedef {{ file: string | null, label: string, qty?: number }} VipKitSlot */
/**
 * @typedef {Object} VipKitPlan
 * @property {string} folder
 * @property {'rust' | 'grid'} [layout]
 * @property {VipKitSlot[]} [belt]
 * @property {VipKitSlot[]} [wear]
 * @property {VipKitSlot} [backpack] Mochila nativa do Rust (smallbackpack / largebackpack)
 * @property {(VipKitSlot | null)[]} [main]
 * @property {VipKitSlot[]} [hotbar]
 * @property {(VipKitSlot | null)[]} [inventory]
 * @property {VipKitSlot[]} [armor]
 */

const LABEL_OVERRIDES = {
    "5.56.webp": "Munição 5.56",
    "5.56-INCENCIARIA.webp": "Munição 5.56 incendiária",
    "9MM.webp": "Munição 9 mm",
    "Ak-Gelo.png": "LR-300 (Gelo)",
    "AK.webp": "AK-47",
    "BOLT.webp": "Rifle de precisão",
    "MP5.webp": "Rifle de assalto (MP5)",
    "9MM.webp": "Munição 9 mm",
    "M39_Rifle_icon.webp": "Fuzil M39",
    "SEMI.webp": "Rifle semiautomático",
    "L96.webp": "Rifle L96",
    "MEDKIT.webp": "Kits médicos",
    "SERINGA.webp": "Seringas médicas",
    "CLOTH.webp": "Panos",
    "SCRAP.webp": "Sucata",
    "GRANADA.webp": "Granada F1",
    "MESA-3.webp": "Mesa de trabalho nível 3",
    "barricade.wood.cover.webp": "Barricadas de madeira",
    "wall.external.high.stone.png": "Muro alto de pedra",
    "Sleeping_Bag_icon.webp": "Saco de dormir",
    "Supply_Signal_icon.webp": "Airdrop",
    "electric.generator.small.png": "Gerador",
    "arcade.machine.chippy.png": "Fliperama Chippy",
    "icethrone.png": "Trono de gelo",
    "easter.goldegg.png": "Ovo de ouro",
    "Large_Present_icon.webp": "Grande presente",
    "Cooked_Chicken_icon.webp": "Frango assado",
    "maxhealthtea.pure.png": "Chá de cura puro",
    "oretea.pure.png": "Chá de minério puro",
    "scraptea.pure.png": "Chá de sucata puro",
    "woodtea.pure.png": "Chá de madeira puro",
    "roadsign.gloves.png": "Luvas de sinal de trânsito",
    "tactical.gloves.png": "Luvas táticas",
    "Ice-Facemask.png": "Máscara de gelo",
    "Ice-Chestplate.png": "Colete de gelo",
    "Wood_Armor_Pants_icon.webp": "Saia de placas (madeira)",
    "Large_Wood_Box_icon.webp": "Caixa de madeira grande",
    "Smoke_Grenade_icon.webp": "Granada de fumo",
    "testgenerator.webp": "Gerador de teste",
    "smallbackpack.png": "Mochila Pequena",
    "largebackpack.png": "Mochila Grande",
};

function labelFromFile(file) {
    if (!file) return "—";
    if (LABEL_OVERRIDES[file]) return LABEL_OVERRIDES[file];
    return file
        .replace(/\.(webp|png|jpe?g)$/i, "")
        .replace(/_icon/gi, "")
        .replace(/\./g, " ")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function slot(file, label, qty) {
    const s = { file, label: label ?? labelFromFile(file) };
    if (qty != null && qty !== 1) s.qty = qty;
    return s;
}

const MAIN_SLOTS = 24;

/** Grelha 6×4 com itens em índices fixos (estilo editor de kits Rust). */
function mainGrid24(entries) {
    const grid = Array(MAIN_SLOTS).fill(null);
    for (const { at, file, label, qty } of entries) {
        if (at >= 0 && at < MAIN_SLOTS) {
            grid[at] = slot(file, label, qty);
        }
    }
    return grid;
}

function iconUrl(folder, file) {
    if (!file || !folder) return null;
    const enc = encodeURIComponent(file);
    return publicAsset(`/images/Vips_rustIcones/${folder}/${enc}`);
}

/** Inventário com todos os ícones da pasta (grelha 6 colunas, slots vazios no fim). */
function inventoryFromFiles(files) {
    const slots = files.map((file) => slot(file));
    const out = [...slots];
    while (out.length % INV_COLS !== 0) out.push(null);
    return out;
}

/** @type {Record<string, VipKitPlan>} */
export const VIP_KIT_PLANS = {
    bronze: {
        folder: VIP_KIT_FOLDERS.bronze,
        layout: "rust",
        /** Cinto (belt) — armas e consumíveis rápidos */
        belt: [
            slot("SEMI.webp", "Rifle semiautomático", 1),
            slot("SERINGA.webp", "Seringas médicas", 10),
            slot("MEDKIT.webp", "Kits médicos", 3),
            slot("Bandage_icon.webp", "Bandagens", 13),
            slot("barricade.wood.cover.webp", "Barricadas de madeira", 10),
            { file: null, label: "—" },
        ],
        /** Roupa (wear) — fila horizontal, ordem visual do kit in-game */
        backpack: slot("smallbackpack.png", "Mochila Pequena"),
        wear: [
            slot("Bandana_Mask_icon.webp", "Bandana"),
            slot("Hoodie_icon.webp", "Blusão"),
            slot("Boots_icon.webp", "Botas"),
            slot("Pants_icon.webp", "Calça"),
            slot("Jacket_icon.webp", "Jaqueta"),
            slot("Wood_Armor_Pants_icon.webp", "Saia de placas"),
            slot("Riot_Helmet_icon.webp", "Capacete de manifestação"),
        ],
        /** Inventário principal 6×4 — recursos na última fila (como no servidor) */
        main: mainGrid24([
            { at: 18, file: "5.56.webp", label: "Munição 5.56", qty: 200 },
            { at: 19, file: "CLOTH.webp", label: "Panos", qty: 50 },
            { at: 20, file: "Sleeping_Bag_icon.webp", label: "Saco de dormir", qty: 1 },
        ]),
    },
    prata: {
        folder: VIP_KIT_FOLDERS.prata,
        layout: "rust",
        belt: [
            slot("MP5.webp", "Rifle de assalto", 1),
            slot("M39_Rifle_icon.webp", "Fuzil M39", 1),
            slot("SERINGA.webp", "Seringas médicas", 15),
            slot("MEDKIT.webp", "Kits médicos", 10),
            slot("Bandage_icon.webp", "Bandagens", 15),
            slot("barricade.wood.cover.webp", "Barricadas de madeira", 10),
        ],
        backpack: slot("largebackpack.png", "Mochila Grande"),
        wear: [
            slot("roadsign.gloves.png", "Luvas de sinal de trânsito"),
            slot("Jacket_icon.webp", "Jaqueta"),
            slot("Boots_icon.webp", "Botas"),
            slot("Hoodie_icon.webp", "Blusão"),
            slot("Road_Sign_Kilt_icon.webp", "Saia de placas de trânsito"),
            slot("Coffee_Can_Helmet_icon.webp", "Capacete de lata de café"),
            slot("Pants_icon.webp", "Calça"),
        ],
        main: mainGrid24([
            { at: 18, file: "9MM.webp", label: "Munição 9 mm", qty: 200 },
            { at: 19, file: "5.56-INCENCIARIA.webp", label: "Munição 5.56 incendiária", qty: 100 },
            { at: 20, file: "CLOTH.webp", label: "Panos", qty: 100 },
            { at: 21, file: "Sleeping_Bag_icon.webp", label: "Saco de dormir", qty: 1 },
            { at: 22, file: "Sleeping_Bag_icon.webp", label: "Saco de dormir", qty: 1 },
        ]),
    },
    ouro: {
        folder: VIP_KIT_FOLDERS.ouro,
        layout: "rust",
        belt: [
            slot("AK.webp", "Rifle de assalto", 1),
            slot("BOLT.webp", "Rifle de precisão", 1),
            slot("SERINGA.webp", "Seringas médicas", 20),
            slot("MEDKIT.webp", "Kits médicos", 10),
            slot("Bandage_icon.webp", "Bandagens", 100),
            slot("wall.external.high.stone.png", "Muro alto de pedra", 20),
        ],
        backpack: slot("largebackpack.png", "Mochila Grande"),
        wear: [
            slot("tactical.gloves.png", "Luvas táticas"),
            slot("Boots_icon.webp", "Botas"),
            slot("Hoodie_icon.webp", "Blusão"),
            slot("Pants_icon.webp", "Calça"),
            slot("Road_Sign_Kilt_icon.webp", "Saia de placas de trânsito"),
            slot("Metal_Facemask_icon.webp", "Máscara de metal"),
            slot("Metal_Chest_Plate_icon.webp", "Colete de metal"),
        ],
        main: mainGrid24([
            { at: 16, file: "Pumpkin_icon.webp", label: "Abóboras", qty: 25 },
            { at: 17, file: "scraptea.pure.png", label: "Chá de sucata puro", qty: 1 },
            { at: 18, file: "5.56.webp", label: "Munição 5.56", qty: 200 },
            { at: 19, file: "5.56-INCENCIARIA.webp", label: "Munição 5.56 incendiária", qty: 100 },
            { at: 20, file: "CLOTH.webp", label: "Panos", qty: 200 },
            { at: 21, file: "Sleeping_Bag_icon.webp", label: "Saco de dormir", qty: 5 },
            { at: 22, file: "oretea.pure.png", label: "Chá de minério puro", qty: 1 },
            { at: 23, file: "maxhealthtea.pure.png", label: "Chá de cura puro", qty: 5 },
        ]),
    },
    /** Kit "[VIP] Supporter" (kits.vipapoiador) — posições iguais ao JSON do plugin Kits. */
    apoiador: {
        folder: VIP_KIT_FOLDERS.apoiador,
        layout: "rust",
        belt: [
            slot("rifle.ak.webp", "Rifle de Assalto", 1),
            slot("rifle.l96.webp", "Rifle L96", 1),
            slot("bandage.webp", "Bandagens", 100),
            slot("syringe.medical.webp", "Seringas médicas", 100),
            slot("largemedkit.webp", "Kits médicos grandes", 3),
            slot("bigcatmeat.cooked.webp", "Carne de felino cozinhada", 100),
        ],
        backpack: slot("largebackpack.png", "Mochila Grande"),
        wear: [
            slot("shoes.boots.webp", "Botas"),
            slot("hoodie.webp", "Blusão"),
            slot("roadsign.kilt.webp", "Saia de Placas de Trânsito"),
            slot("tactical.gloves.png", "Luvas Táticas"),
            slot("metal.facemask.webp", "Máscara de Metal"),
            slot("metal.plate.torso.webp", "Colete de Metal"),
            slot("pants.webp", "Calça"),
        ],
        main: mainGrid24([
            { at: 0, file: "ammo.rifle.incendiary.webp", label: "Munição 5.56 incendiária", qty: 100 },
            { at: 1, file: "ammo.rifle.webp", label: "Munição 5.56", qty: 200 },
            { at: 2, file: "metal.refined.webp", label: "Metal de Alta Qualidade", qty: 1000 },
            { at: 3, file: "wiretool.png", label: "Ferramenta de Fio", qty: 1 },
            { at: 4, file: "ammo.rocket.sam.png", label: "Munição Antiaérea", qty: 1000 },
            { at: 5, file: "autoturret.png", label: "Sentinela Automática", qty: 1 },
            { at: 6, file: "electric.furnace.png", label: "Fornalha Elétrica", qty: 1 },
            { at: 7, file: "electric.furnace.png", label: "Fornalha Elétrica", qty: 1 },
            { at: 8, file: "gunpowder.webp", label: "Pólvora", qty: 3000 },
            { at: 9, file: "supply.signal.webp", label: "Sinalizador de Suprimentos", qty: 1 },
            { at: 10, file: "samsite.png", label: "Local de Sistema Antiaéreo", qty: 1 },
            { at: 11, file: "autoturret.png", label: "Sentinela Automática", qty: 1 },
            { at: 12, file: "electric.furnace.png", label: "Fornalha Elétrica", qty: 1 },
            { at: 13, file: "electric.furnace.png", label: "Fornalha Elétrica", qty: 1 },
            { at: 14, file: "furnace.large.webp", label: "Fornalha Grande", qty: 1 },
            { at: 15, file: "coffin.storage.webp", label: "Caixão", qty: 1 },
            { at: 16, file: "samsite.png", label: "Local de Sistema Antiaéreo", qty: 1 },
            { at: 17, file: "autoturret.png", label: "Sentinela Automática", qty: 1 },
            { at: 18, file: "electric.furnace.png", label: "Fornalha Elétrica", qty: 1 },
            { at: 19, file: "electric.furnace.png", label: "Fornalha Elétrica", qty: 1 },
            { at: 20, file: "furnace.large.webp", label: "Fornalha Grande", qty: 1 },
            { at: 21, file: "coffin.storage.webp", label: "Caixão", qty: 1 },
            { at: 22, file: "electrical.branch.png", label: "Ramificador Elétrico", qty: 100 },
            { at: 23, file: "electric.generator.small.png", label: "Gerador Pequeno", qty: 1 },
        ]),
    },
};

/**
 * @param {string} tierId
 * @returns {VipKitPlan | null}
 */
export function getVipKitPlan(tierId) {
    return VIP_KIT_PLANS[tierId] ?? null;
}

/**
 * @param {string} tierId
 * @param {string | null} file
 */
export function getVipKitIconSrc(tierId, file) {
    const plan = getVipKitPlan(tierId);
    if (!plan || !file) return null;
    return iconUrl(plan.folder, file);
}
